import { FormResponses, CalculationResult } from '@/types/solidarity';
import { getPercentileForScore } from './percentileMapping';

export const calculateSolidarityIndex = (responses: FormResponses): CalculationResult => {
  // Calculate volunteering index (0-5 points)
  const index_volunteer = calculateVolunteeringIndex(responses);
  // Calculate charity index (0-5 points)
  const index_charity = calculateCharityIndex(responses);
  // Calculate attitudes index (0-5 points)
  const index_attitudes = calculateAttitudesIndex(responses);
  // Calculate total index (average of three sub-indexes)
  const index_total = (index_volunteer + index_charity + index_attitudes) / 3;
  // Rescale to 0-100 for display
  const index_rescaled = Math.round(index_total * 20);
  console.log('Calculation details:', {
    index_volunteer,
    index_charity,
    index_attitudes,
    index_total,
    index_rescaled
  });

  // Use the new percentile mapping system
  const { percentile, text } = getPercentileForScore(index_rescaled);
  const result: CalculationResult = {
    index_volunteer,
    index_charity,
    index_attitudes,
    index_total,
    index_rescaled,
    percentile: percentile || 0,
    custom_text: text
  };
  
  console.log('Final result:', result);
  return result;
};

const calculateVolunteeringIndex = (responses: FormResponses): number => {
  let points = 0;
  const { volunteering } = responses;

  console.log('Volunteering calculation - volunteering:', volunteering);
  // Point 1: Q02 is "Ano"
  if (volunteering.Q02 === 1) {
    points += 1;
    console.log('Volunteering point 1: Q02 = 1');
  }
  // Point 2: Q04_A OR Q04_B is "Ano"
  if (volunteering.Q04_A || volunteering.Q04_B) {
    points += 1;
    console.log('Volunteering point 2: Q04_A or Q04_B = true');
  }
  // Point 3: Q04_C is "Ano"
  if (volunteering.Q04_C) {
    points += 1;
    console.log('Volunteering point 3: Q04_C = true');
  }
  // Point 4: Q10 >= 6 hours
  if (volunteering.Q10 && volunteering.Q10 >= 6) {
    points += 1;
    console.log('Volunteering point 4: Q10 =', volunteering.Q10);
  }
  // Point 5: Q12 is frequent (1, 2, or 3)
  if (volunteering.Q12 && (volunteering.Q12 === 1 || volunteering.Q12 === 2 || volunteering.Q12 === 3)) {
    points += 1;
    console.log('Volunteering point 5: Q12 =', volunteering.Q12);
  }

  console.log('Total volunteering points:', points);
  return Math.min(points, 5);
};

const calculateCharityIndex = (responses: FormResponses): number => {
  let points = 0;
  const { donations } = responses;

  console.log('Charity calculation - donations:', donations);
  // Point 1: D01_new is "Ano"
  if (donations.D01_new === 1) {
    points += 1;
    console.log('Charity point 1: D01_new = 1');
  }
  // Point 2: D07 is "Ano"
  if (donations.D07 === 1) {
    points += 1;
    console.log('Charity point 2: D07 = 1');
  }
  // Point 3: D13 is regular giving (1 or 2)
  if (donations.D13 === 1 || donations.D13 === 2) {
    points += 1;
    console.log('Charity point 3: D13 =', donations.D13);
  }
  // Point 4: D16 is "Ano" (blood donation)
  if (donations.D16 === 1) {
    points += 1;
    console.log('Charity point 4: D16 = 1');
  }

  // Point 5: D17_F is NOT checked (meaning gave non-financial gifts)
  const hasNonFinancialGifts = donations.D17_A || donations.D17_B || donations.D17_C || donations.D17_D || donations.D17_E;
  if (!donations.D17_F && hasNonFinancialGifts) {
    points += 1;
    console.log('Charity point 5: D17_F not checked and has non-financial gifts');
  }

  console.log('Total charity points:', points);
  return Math.min(points, 5);
};

const calculateAttitudesIndex = (responses: FormResponses): number => {
  const { attitudes } = responses;

  console.log('Attitudes calculation - attitudes:', attitudes);
  // Collect all P01 responses
  const attitudeResponses = [
    attitudes.P01_A,
    attitudes.P01_B,
    attitudes.P01_C,
    attitudes.P01_D,
    attitudes.P01_E,
    attitudes.P01_F,
    attitudes.P01_G
  ];
  // Filter out undefined responses
  const validResponses = attitudeResponses.filter(response => response !== undefined) as number[];

  console.log('Valid attitude responses:', validResponses);
  if (validResponses.length === 0) {
    return 0;
  }
  // Reverse scale: (6 - x) for each response
  const reversedResponses = validResponses.map(response => 6 - response);
  console.log('Reversed responses:', reversedResponses);
  // Calculate average of reversed responses
  const average = reversedResponses.reduce((sum, value) => sum + value, 0) / reversedResponses.length;
  console.log('Average of reversed responses:', average);
  // Rescale to 0-5: (average - 1) * (5/4)
  const rescaled = (average - 1) * (5 / 4);
  console.log('Rescaled attitudes index:', rescaled);
  return Math.max(0, Math.min(rescaled, 5));
};